/**
 * @file: useUserInfoManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import {
  useAuthContext,
  useCurrentUserDangerously,
} from '@euler/app/flows/auth/context';
import {
  UserProfile,
  UserProfileUpdateInfo,
  UserProfileUpdateParams,
} from '@euler/model';
import { useServiceFactory } from '@euler/services/factory';
import { showAlert } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import produce from 'immer';
import { equals } from 'ramda';
import { useCallback, useState } from 'react';
import { useSharedValue } from 'react-native-reanimated';

export const useUserProfileManager = () => {
  const context = useAuthContext();
  const { userService, mediaFileService } = useServiceFactory();
  const [authState, setAuthState] = useBehaviorSubject(context.authState$);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const uploadProgress = useSharedValue(0);
  const userInfo = useCurrentUserDangerously();

  const updateAsync = usePersistFn(
    async (
      params: UserProfileUpdateParams,
      onError?: ((error: Error) => void) | null,
    ) => {
      if (!authState.isAuthenticated) return;
      const { user } = userInfo;
      const updateInfo: UserProfileUpdateInfo = {};
      if (params.name != null && !equals(params.name, user.name)) {
        updateInfo.name = params.name;
      }
      if (params.nick != null && !equals(params.nick, user.nick)) {
        updateInfo.nick = params.nick;
      }
      if (params.gender != null && !equals(params.gender, user.gender)) {
        updateInfo.gender = params.gender;
      }
      if (params.title != null && !equals(params.title, user.jobTitle)) {
        updateInfo.title = params.title;
      }
      if (params.avatar != null && !equals(params.avatar, user.avatar)) {
        updateInfo.avatar = params.avatar;
      }
      if (params.email != null && !equals(params.email, user.email)) {
        updateInfo.email = params.email;
      }
      if (params.mobile != null && !equals(params.mobile, user.mobile)) {
        updateInfo.mobile = params.mobile;
      }
      if (!Object.keys(updateInfo).length) {
        return;
      }
      try {
        await userService.updateProfile(params);
        const updatedAuthState = produce(authState, draft => {
          const { title, ...rest } = updateInfo;
          Object.assign(draft.authenticatedUserInfo.user, rest);
          if (title != null) draft.authenticatedUserInfo.user.jobTitle = title;
        });
        setAuthState(updatedAuthState);
      } catch (e) {
        if (onError != null) {
          onError(e as Error);
        } else {
          void showAlert('出错了', (e as Error).message);
        }
      }
    },
  );

  const setProfileAvatar = usePersistFn((value: string | undefined) => {
    if (!authState.isAuthenticated) return;
    const updatedAuthState = produce(authState, draft => {
      draft.authenticatedUserInfo.user.avatar = value;
    });
    setAuthState(updatedAuthState);
  });

  const uploadAndSetAvatarAsync = useCallback(
    async (localFileUri: string) => {
      const originalAvatarUrl = userInfo.user.avatar;
      setIsUploadingAvatar(true);
      setProfileAvatar(localFileUri);
      try {
        uploadProgress.value = 0;
        const result = await mediaFileService.upload(localFileUri, {
          blurhash: true,
          onProgress: e => {
            uploadProgress.value = e.total > 0 ? e.loaded / e.total : 0;
          },
        });
        await updateAsync(
          {
            avatar: result.url,
          },
          error => {
            setProfileAvatar(originalAvatarUrl);
            void showAlert('出错了', error.message);
          },
        );
      } finally {
        setIsUploadingAvatar(false);
        uploadProgress.value = 0;
      }
    },
    [
      mediaFileService,
      setProfileAvatar,
      updateAsync,
      uploadProgress,
      userInfo.user.avatar,
    ],
  );

  return {
    profile: userInfo as UserProfile,
    isUploadingAvatar,
    uploadProgress,
    updateAsync,
    uploadAndSetAvatarAsync,
  };
};
